import type { TestDataProps } from "@repo/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CashierTradingAccountMargin,
  CurrencyPrimitive,
  FeeSettingsResponse,
} from "@/api/cashier";
import globe from "@/assets/icons/globe-icon.svg";
import { AccountItem } from "@/components/account-list/account-item/AccountItem";
import { ExchangeRate as ExchangeRateComp } from "@/components/ExchangeRate";
import { BaseModal } from "@/components/general/base-modal/BaseModal";
import { Icon } from "@/components/general/icon/Icon";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import type { EIcon } from "@/utils/enums";
import { moneyToMajorUnits } from "@/utils/helpers";
import style from "./ConfirmTransferModal.module.css";

type ExchangeRate = {
  from_currency_code: string;
  to_currency_code: string;
  rate: number;
};

type ConfirmTransferModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onTimeout?: () => void;
  onConfirm: () => void;
  isConfirmLoading?: boolean;
  fromTransferData: {
    account: CashierTradingAccountMargin;
    currency: CurrencyPrimitive;
    amount: number;
  };
  toTransferData: {
    account: CashierTradingAccountMargin;
    currency: CurrencyPrimitive;
    amount: number;
  };
  arrivalTime: string;
  exchangeRate?: ExchangeRate;
  fee?: FeeSettingsResponse;
} & TestDataProps;

const CONFIRMATION_TRANSFER_IN_SECONDS = 60;

export const ConfirmTransferModal = ({
  isOpen,
  onClose,
  onTimeout,
  onConfirm,
  isConfirmLoading = false,
  fromTransferData,
  toTransferData,
  arrivalTime,
  exchangeRate,
  fee,
  "data-testid": testId,
}: ConfirmTransferModalProps) => {
  const { t, i18n } = useTranslation();
  const { getValueFromBankingAmount } = useFormatAmount();
  const modalTestId = testId ?? "confirm-transfer-modal";

  const [secondsLeft, setSecondsLeft] = useState(
    CONFIRMATION_TRANSFER_IN_SECONDS
  );
  const countDownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasTimeout = secondsLeft <= 0;

  const differentCurrency = useMemo(
    () => fromTransferData.currency.code !== toTransferData.currency.code,
    [fromTransferData, toTransferData]
  );

  const hasExceedFreeMargin = useMemo(() => {
    return (
      fromTransferData.amount >
      (getValueFromBankingAmount(fromTransferData.account.margin) ?? 0)
    );
  }, [fromTransferData, getValueFromBankingAmount]);

  const clearConfirmationInterval = useCallback(() => {
    if (countDownIntervalRef.current) {
      clearInterval(countDownIntervalRef.current);
    }
  }, []);

  const { formatFee, formatAmount } = useFormatAmount();

  const refreshTimeout = useCallback(() => {
    clearConfirmationInterval();
    setSecondsLeft(CONFIRMATION_TRANSFER_IN_SECONDS);
  }, [clearConfirmationInterval]);

  const handleCloseModal = useCallback(() => {
    refreshTimeout();
    onClose();
  }, [onClose, refreshTimeout]);

  useEffect(() => {
    if (hasExceedFreeMargin) handleCloseModal();
  }, [hasExceedFreeMargin, handleCloseModal]);

  useEffect(() => {
    if (!differentCurrency) return;

    if (hasTimeout) {
      clearConfirmationInterval();
      return;
    }

    if (!isOpen) {
      clearConfirmationInterval();
      setSecondsLeft(CONFIRMATION_TRANSFER_IN_SECONDS);
      return;
    }
    countDownIntervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          handleCloseModal();
          if (onTimeout) onTimeout();
          return CONFIRMATION_TRANSFER_IN_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countDownIntervalRef.current)
        clearInterval(countDownIntervalRef.current);
    };
  }, [
    clearConfirmationInterval,
    onTimeout,
    isOpen,
    hasTimeout,
    handleCloseModal,
    differentCurrency,
  ]);

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={t("transfer.confirm_transfer")}
      testId={modalTestId}
      description={
        <div>
          {differentCurrency && (
            <span data-testid={`${modalTestId}-countdown`}>
              {t("transfer.transfer_confirmation_description", {
                seconds: secondsLeft,
              })}
            </span>
          )}
          {hasExceedFreeMargin && (
            <p
              className={style["transfer-confirmation-error"]}
              data-testid={`${modalTestId}-error`}
            >
              {t("transfer.errors.exceeds_limit_amount", {
                maxlimit: formatAmount(
                  moneyToMajorUnits(fromTransferData.account.margin),
                  fromTransferData.currency
                ),
              })}
            </p>
          )}
        </div>
      }
      submitButtonContent={t("global.buttons.confirm")}
      isSubmitButtonDisabled={
        hasTimeout || hasExceedFreeMargin || isConfirmLoading
      }
      isSubmitButtonLoading={isConfirmLoading}
      onSubmit={onConfirm}
    >
      <div
        className={style["transfer-details-container"]}
        data-testid={`${modalTestId}-details`}
      >
        <div data-testid={`${modalTestId}-from-section`}>
          <div
            className={style["transfer-account-section"]}
            data-testid={`${modalTestId}-from-account`}
          >
            <p
              className={style["transfer-label"]}
              data-testid={`${modalTestId}-from-label`}
            >
              {t("transfer.accounts.transfer_from")}
            </p>
            <AccountItem
              account={fromTransferData.account}
              preview
              data-testid={`account-from-transfer-${testId}`}
            />
          </div>

          <div
            className={style["transfer-amount-block"]}
            data-testid={`${modalTestId}-from-amount-block`}
          >
            <div
              className={style["transfer-currency"]}
              data-testid={`${modalTestId}-from-currency`}
            >
              <Icon
                name={
                  `flags/${fromTransferData.currency.code.toLowerCase()}-flag` as EIcon
                }
                fallback={globe}
              />
              <span data-testid={`${modalTestId}-from-currency-code`}>
                {fromTransferData.currency.code}
              </span>
            </div>
            <p
              className={style["transfer-value"]}
              data-testid={`${modalTestId}-from-amount`}
            >
              {formatAmount(
                fromTransferData.amount,
                fromTransferData.currency,
                i18n.language
              )}
            </p>
          </div>
        </div>

        <div data-testid={`${modalTestId}-to-section`}>
          <div
            className={style["transfer-account-section"]}
            data-testid={`${modalTestId}-to-account`}
          >
            <p
              className={style["transfer-label"]}
              data-testid={`${modalTestId}-to-label`}
            >
              {t("transfer.accounts.transfer_to")}
            </p>
            <AccountItem
              account={toTransferData.account}
              preview
              data-testid={`account-to-transfer-${testId}`}
            />
          </div>

          <div
            className={style["transfer-amount-block"]}
            data-testid={`${modalTestId}-to-amount-block`}
          >
            <div
              className={style["transfer-currency"]}
              data-testid={`${modalTestId}-to-currency`}
            >
              <Icon
                name={
                  `flags/${toTransferData.currency.code.toLowerCase()}-flag` as EIcon
                }
                fallback={globe}
              />
              <span data-testid={`${modalTestId}-to-currency-code`}>
                {toTransferData.currency.code}
              </span>
            </div>
            <p
              className={style["transfer-value"]}
              data-testid={`${modalTestId}-to-amount`}
            >
              {formatAmount(
                toTransferData.amount,
                toTransferData.currency,
                i18n.language
              )}
            </p>
          </div>
        </div>
      </div>

      <div data-testid={`${modalTestId}-summary`}>
        {exchangeRate &&
          exchangeRate.from_currency_code !== exchangeRate.to_currency_code && (
            <div
              className={style["transfer-summary-block"]}
              data-testid={`${modalTestId}-exchange-rate-block`}
            >
              <p
                className={style["transfer-summary-label"]}
                data-testid={`${modalTestId}-exchange-rate-label`}
              >
                {t("transfer.exchange_rate")}
              </p>
              <p
                className={style["transfer-value"]}
                data-testid={`${modalTestId}-exchange-rate-value`}
              >
                <ExchangeRateComp
                  unit={1}
                  currency={fromTransferData.currency}
                  exchangeRate={exchangeRate.rate}
                  exchangeCurrency={toTransferData.currency}
                />
              </p>
            </div>
          )}
        {fee && differentCurrency && (
          <div
            className={style["transfer-summary-block"]}
            data-testid={`${modalTestId}-fee-block`}
          >
            <p
              className={style["transfer-summary-label"]}
              data-testid={`${modalTestId}-fee-label`}
            >
              {t("transfer.fee")}
            </p>
            <p
              className={style["transfer-value"]}
              data-testid={`${modalTestId}-fee-value`}
            >
              {formatFee(fee, fromTransferData.currency)}
            </p>
          </div>
        )}
        <div
          className={style["transfer-summary-block"]}
          data-testid={`${modalTestId}-arrival-time-block`}
        >
          <p
            className={style["transfer-summary-label"]}
            data-testid={`${modalTestId}-arrival-time-label`}
          >
            {t("transfer.arrival_time")}
          </p>
          <p
            className={style["transfer-value"]}
            data-testid={`${modalTestId}-arrival-time-value`}
          >
            {arrivalTime}
          </p>
        </div>
      </div>
    </BaseModal>
  );
};
